import type { NavigationEntry } from './navigation-capture';
import { takeFullSnapshot } from './recorder';

// A hash-only change (#anchor) doesn't swap the page DOM, so it needs no
// re-snapshot — only a real path/query change re-renders the route.
export function isHashOnlyChange(from: string, to: string): boolean {
  try {
    const a = new URL(from);
    const b = new URL(to);
    return (
      a.origin === b.origin &&
      a.pathname === b.pathname &&
      a.search === b.search
    );
  } catch {
    return false;
  }
}

export interface RouteSnapshotScheduler {
  schedule(entry: NavigationEntry): void;
  cancel(): void;
}

/**
 * rrweb 2.x snapshots only at record() init and every checkoutEveryNms, so an
 * SPA route change leaves the new page without a FullSnapshot — deep-seeking to
 * a friction there replays off the previous route's DOM. This re-anchors each
 * real route with a checkout snapshot. The snapshot is deferred a double rAF
 * (past the next paint) so a synchronously-rendered route is captured settled
 * rather than mid-swap; a route that paints asynchronously (data fetch /
 * Suspense / lazy chunk) may still snapshot a loading state — an accepted
 * limitation. Debounced so a burst of navigations only snapshots the last.
 * `snapshot` is injectable for tests.
 */
export function createRouteSnapshotScheduler(
  snapshot: () => void = takeFullSnapshot
): RouteSnapshotScheduler {
  let raf: number | null = null;

  const cancel = (): void => {
    if (raf !== null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  };

  return {
    cancel,
    schedule(entry: NavigationEntry): void {
      if (typeof requestAnimationFrame === 'undefined') return;
      if (isHashOnlyChange(entry.from, entry.to)) return;
      cancel();
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          raf = null;
          snapshot();
        });
      });
    },
  };
}
